/* eslint-disable react/no-array-index-key */
import React from 'react';
import PropTypes from 'prop-types';
import { Stack } from 'office-ui-fabric-react';
import clsx from 'clsx';
import { mergeStyleSets } from '@uifabric/merge-styles';
import { layout } from '../../theme.style';

const childStyle = (smCols, mdCols, lgCols, gap, nowrap, shrink) => {
  const normalizedGap = gap.toString().split(' ');
  const columnGap = normalizedGap[1] || normalizedGap[0];
  const flexGrow = shrink ? 0 : 1;
  const flexShrink = shrink ? 1 : 0;

  const wrappedStyle = {
    flex: `${flexGrow} ${flexShrink} calc(${100 / smCols}% - ${columnGap}px)`,
    maxWidth: `calc(${100 / smCols}% - ${columnGap}px)`,
    selectors: {
      [layout.breakpoints.min.md]: {
        flex: `${flexGrow} ${flexShrink} calc(${100 / mdCols}% - ${columnGap}px)`,
        maxWidth: `calc(${100 / mdCols}% - ${columnGap}px)`,
      },
      [layout.breakpoints.min.lg]: {
        flex: `${flexGrow} ${flexShrink} calc(${100 / lgCols}% - ${columnGap}px)`,
        maxWidth: `calc(${100 / lgCols}% - ${columnGap}px)`,
      },
    },
  };

  const nowrapStyle = {
    flex: '1 0 100%',
  };

  return mergeStyleSets({
    childItem: {
      ...(nowrap ? nowrapStyle : wrappedStyle),
    },
  });
};

/*
    Purpoose: To provide an abstraction of the FluentUI Stack component with more granular control of row wrapping using flexbox.
    Usage:
        <StackGrid
            smCols={1}
            mdCols={2}
            lgCols={3}
            gap={32}
            childClassName={styles.childClass}
            parentClassName={styles.parentClass}
        >
            <Component1 />
            <Component2 nowrap />
            ...
        </StackGrid>

    Note: This will not go recursive into children, if you want multiple children treated as a single child just wrap
          them in something like a div, span, or even Stack.Item
*/
const StackGrid = ({
  smCols,
  mdCols,
  lgCols,
  gap,
  children,
  parentClassName,
  childClassName,
  shrink,
  ...other
}) => {
  const safeChildren = children && children.length === undefined ? [children] : children;

  return (
    <Stack horizontal wrap tokens={{ childrenGap: gap }} className={parentClassName} {...other}>
      {safeChildren &&
        safeChildren
          .filter(Boolean)
          .flat() // fixes issue where you render a list in a StackGrid. you would expect the list to be flattened
          .map((child, index) => {
            if (child.props && child.props.type === 'hidden') return child;

            return (
              <Stack.Item
                className={clsx(
                  childStyle(smCols, mdCols, lgCols, gap, child.props.nowrap, shrink).childItem,
                  childClassName,
                )}
                key={index}
              >
                {child}
              </Stack.Item>
            );
          })}
    </Stack>
  );
};

StackGrid.propTypes = {
  /**
   * className of the containing parent component
   */
  parentClassName: PropTypes.string,
  /**
   * className of every child component
   */
  childClassName: PropTypes.string,
  /**
   * Add this to any child component:
   * nowrap (bool): Set this on any child you don't want wrapped, useful for things like header rows.
   */
  children: PropTypes.node.isRequired,
  /**
   * Number of columns at the small breakpoint
   */
  smCols: PropTypes.number,
  /**
   * Number of columns at the medium breakpoint
   */
  mdCols: PropTypes.number,
  /**
   * Number of columns at the large breakpoint and up
   */
  lgCols: PropTypes.number,
  /**
   * Number of pixels between components when wrapped. Same as `childrenGap` token
   * for Fluent's Stack component. Can be single value like `32` or `'20 30'`.
   * See https://developer.microsoft.com/en-us/fluentui#/controls/web/stack#IStackTokens
   */
  gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Allow children to shrink to their exact specified size, rather than growing to
   * take the full width of their container.
   */
  shrink: PropTypes.bool,
};

StackGrid.defaultProps = {
  parentClassName: '',
  childClassName: '',
  smCols: 1,
  mdCols: 1,
  lgCols: 1,
  gap: '24 32',
  shrink: false,
};

export default StackGrid;
